import React, { lazy, Suspense, useEffect } from "react";

import LoadingPage from "Dashboard/Components/LoadingPage";
import { connect } from "react-redux";
import { Switch, Route } from "react-router-dom";
import { FourOhFour } from "ZeroRuby";
import installIntercomChat from "ZeroRuby/snippets/intercomChat";

import { useSiteConfig } from "Common/Redux/siteConfig";
import ScrollToTop from "Common/ScrollToTop";
import ThemeInstaller from "Common/ThemeInstaller";

import { isInitialDataLoaded } from "./selectors";

import type { State } from "./createStore";
import type { Match } from "react-router-dom";

const AddDependentPage = lazy(() => import("EmployeeDashboard/DependentApplication/AddDependent" /* webpackChunkName: "AddDependent" */));
const AddedDependentPage = lazy(() => import("./Components/AddedDependentPage/index" /* webpackChunkName: "AddDependent" */));
const ClaimShowPage = lazy(() => import("./Components/ClaimPage/show" /* webpackChunkName: "ClaimPage" */));
const ClaimsIndexPage = lazy(() => import("./Components/ClaimsPage" /* webpackChunkName: "ClaimsPage" */));
const DependentsPage = lazy(() => import("./Components/DependentsPage" /* webpackChunkName: "DependentsPage" */));
const EditDependentApp = lazy(() => import("./Components/EditDependentApp" /* webpackChunkName: "EditDependentApp" */));
const BeneficiariesView = lazy(() => import("./pages/BeneficiariesView" /* webpackChunkName: "BeneficiariesView" */));
const BeneficiariesEdit = lazy(() => import("./pages/BeneficiariesEdit" /* webpackChunkName: "BeneficiariesEdit" */));
const EmployeeInformationApp = lazy(() => import("./Components/EmployeeInformation/App" /* webpackChunkName: "EmployeeInformationApp" */));
const HomePage = lazy(() => import("./Components/HomePage/HomePage" /* webpackChunkName: "HomePage" */));
const MobileIdCardsPage = lazy(() => import("./Components/MobileIdCardsPage" /* webpackChunkName: "MobileIdCardsPage" */));
const SubmitClaimsPage = lazy(() => import("./Components/SubmitClaimsPage" /* webpackChunkName: "SubmitClaimsPage" */));
const SuccessPage = lazy(() => import("./Components/SuccessPage" /* webpackChunkName: "SuccessPage" */));
const ViewCoveragesPage = lazy(() => import("./Components/ViewCoveragesPage" /* webpackChunkName: "ViewCoveragesPage" */));

type EmployeeDashboardProps = {
  isLoaded: boolean;
  match: Match;
};

const EmployeeDashboardDumb = ({ isLoaded, match }: EmployeeDashboardProps) => {
  const { isLoading: isLoadingSiteConfig } = useSiteConfig();

  useEffect(() => {
    if (isLoaded && !isLoadingSiteConfig) {
      installIntercomChat();
    }
  }, [isLoaded, isLoadingSiteConfig]);

  if (isLoaded && !isLoadingSiteConfig) {
    return (
      <ScrollToTop>
        <Suspense fallback={<LoadingPage />}>
          <>
            <Switch>
              <Route exact path={`${match.url}`} children={<HomePage />} />
              <Route path={`${match.url}/view-coverage`} children={<ViewCoveragesPage />} />
              <Route path={`${match.url}/open-claims`} children={<ClaimsIndexPage />} />
              <Route path={`${match.url}/claims/:id`} children={({ match }) => <ClaimShowPage match={match} />} />
              <Route path={`${match.url}/submit-claims`} children={<SubmitClaimsPage />} />
              <Route path={`${match.url}/mobile-id-cards`} children={<MobileIdCardsPage />} />
              <Route path={`${match.url}/change-dependent-coverage`} children={<DependentsPage />} />
              <Route
                path={`${match.url}/edit-dependent-information/:id`}
                children={({ match, history }) => <EditDependentApp match={match} history={history} />}
              />
              <Route path={`${match.url}/add-dependent`} children={<AddDependentPage />} />
              <Route
                path={`${match.url}/added-employee-dependent/:id`}
                children={({ match, history }) => <AddedDependentPage match={match} history={history} />}
              />
              <Route path={`${match.url}/beneficiaries`}>
                <BeneficiariesView />
              </Route>
              <Route path={`${match.url}/edit-beneficiaries`}>
                <BeneficiariesEdit />
              </Route>
              <Route path={`${match.url}/employee-information`} children={({ match }) => <EmployeeInformationApp match={match} />} />
              <Route path={`${match.url}/success`} children={<SuccessPage />} />
              <Route children={<FourOhFour />} />
            </Switch>
            <ThemeInstaller />
          </>
        </Suspense>
      </ScrollToTop>
    );
  }
  return <LoadingPage />;
};

const mapStateToProps = (state: State) => ({
  isLoaded: isInitialDataLoaded(state),
});

const withRedux = connect(
  mapStateToProps,
  undefined,
  undefined, // This component isn't pure because it relies on the browser URL rather than
  // the Redux store.
  {
    pure: false,
  },
);
export default withRedux(EmployeeDashboardDumb);
